// Lista "Campañas creadas" (email) + pestaña Mensajería (WhatsApp, requiere conectar la API).
import type { JSX } from "react";
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Copy, Folder, MessageCircle, Pencil, Plus, Search, Trash2, Mail } from "lucide-react";
import { api } from "../../../lib/api.ts";
import { useMe } from "../../../lib/auth.ts";
import { Badge, Button, Card, EmptyState, ErrorState, Input, Select, TableSkeleton } from "../../../components/ui.tsx";
import { toast } from "../../../components/toast.tsx";
import { EmailThumb } from "./EmailFrame.tsx";
import { type CampaignRow, type CampaignStatus, fmtDate, pct, STATUS_LABEL } from "./types.ts";

type Channel = "email" | "whatsapp";

export function CampaignList(): JSX.Element {
  const nav = useNavigate();
  const qc = useQueryClient();
  const isAdmin = Boolean(useMe().data?.admin);
  const [channel, setChannel] = useState<Channel>("email");
  const [status, setStatus] = useState<"" | CampaignStatus>("");
  const [folder, setFolder] = useState("");
  const [term, setTerm] = useState("");
  const params = new URLSearchParams({ channel, ...(status ? { status } : {}), ...(folder ? { folder } : {}), ...(term.trim() ? { q: term.trim() } : {}) });
  const list = useQuery({
    queryKey: ["mkt-campaigns", channel, status, folder, term.trim()],
    queryFn: () => api.get<{ items: CampaignRow[]; counts: Record<string, number>; whatsappConnected: boolean }>(`/mkt/campaigns?${params.toString()}`),
    refetchInterval: (q) => (q.state.data?.items.some((c) => c.status === "sending") ? 10_000 : false),
  });
  const folders = useQuery({ queryKey: ["mkt-campaign-folders"], queryFn: () => api.get<{ items: Array<{ folder: string; n: number }> }>("/mkt/campaigns/folders") });

  const create = useMutation({
    mutationFn: () => api.post<{ id: string }>("/mkt/campaigns", {}),
    onSuccess: (r) => { void qc.invalidateQueries({ queryKey: ["mkt-campaigns"] }); nav(`/marketing/campanas/${r.id}/editar`); },
    onError: (e) => toast.error(e.message),
  });
  const dup = useMutation({
    mutationFn: (id: string) => api.post<{ id: string }>(`/mkt/campaigns/${id}/duplicate`),
    onSuccess: (r) => { void qc.invalidateQueries({ queryKey: ["mkt-campaigns"] }); toast.success("Campaña duplicada como borrador."); nav(`/marketing/campanas/${r.id}/editar`); },
    onError: (e) => toast.error(e.message),
  });
  const del = useMutation({
    mutationFn: (id: string) => api.del(`/mkt/campaigns/${id}`),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ["mkt-campaigns"] }); toast.success("Campaña eliminada."); },
    onError: (e) => toast.error(e.message),
  });

  const counts = list.data?.counts ?? {};
  const total = Object.values(counts).reduce((a, b) => a + b, 0);
  const statusTabs: Array<["" | CampaignStatus, string]> = [["", "Todas"], ["sent", "Enviadas"], ["sending", "Enviando"], ["scheduled", "Planificadas"], ["draft", "Borradores"], ["cancelled", "Canceladas"]];

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <div className="inline-flex rounded-md border border-line overflow-hidden text-sm">
          <button type="button" className={`px-3 py-1.5 ${channel === "email" ? "bg-primary-soft text-primary font-medium" : "text-ink-secondary hover:bg-surface-hover"}`} onClick={() => setChannel("email")}>Email</button>
          <button type="button" className={`px-3 py-1.5 ${channel === "whatsapp" ? "bg-primary-soft text-primary font-medium" : "text-ink-secondary hover:bg-surface-hover"}`} onClick={() => setChannel("whatsapp")}>Mensajería (WhatsApp)</button>
        </div>
        <div className="ml-auto" />
        {isAdmin && channel === "email" && <Button variant="primary" onClick={() => create.mutate()} disabled={create.isPending}><Plus size={15} className="mr-1 inline" />Nueva campaña</Button>}
      </div>

      {channel === "whatsapp" && (
        <Card className="p-5 flex gap-4 items-start">
          <MessageCircle className="text-success shrink-0" size={28} />
          <div>
            <div className="font-semibold text-ink">Campañas de mensajería por WhatsApp</div>
            <p className="text-sm text-ink-secondary mt-1">Para enviar campañas por WhatsApp hay que conectar <b>WhatsApp Business API</b> (Meta Cloud API) con un número verificado y plantillas aprobadas. Esta integración aún no está configurada en Tiger.</p>
            <p className="text-xs text-ink-muted mt-2">Las campañas de WhatsApp importadas de Clientify (solo estadísticas) aparecen abajo.</p>
          </div>
        </Card>
      )}

      <Card className="p-0">
        <div className="flex flex-wrap items-center gap-2 px-4 py-3 border-b border-line">
          <div className="flex gap-1 overflow-x-auto scrollbar-thin">
            {statusTabs.map(([k, label]) => (
              <button key={k || "all"} type="button" onClick={() => setStatus(k)}
                className={`px-2.5 py-1 rounded-md text-xs whitespace-nowrap ${status === k ? "bg-primary-soft text-primary font-medium" : "text-ink-secondary hover:bg-surface-hover"}`}>
                {label} <span className="tnum opacity-70">{k ? counts[k] ?? 0 : total}</span>
              </button>
            ))}
          </div>
          <div className="ml-auto flex items-center gap-2">
            {(folders.data?.items.length ?? 0) > 0 && (
              <div className="flex items-center gap-1"><Folder size={14} className="text-ink-muted" />
                <Select className="!py-1 text-xs !w-auto" value={folder} onChange={(e) => setFolder(e.target.value)} aria-label="Carpeta">
                  <option value="">Todas las carpetas</option><option value="__none">Sin carpeta</option>
                  {folders.data?.items.map((f) => <option key={f.folder} value={f.folder}>{f.folder} ({f.n})</option>)}
                </Select>
              </div>
            )}
            <div className="relative"><Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-ink-muted" />
              <Input className="pl-8 !py-1 text-sm w-56" placeholder="Buscar campaña…" value={term} onChange={(e) => setTerm(e.target.value)} />
            </div>
          </div>
        </div>
        {list.isLoading ? <TableSkeleton rows={6} cols={7} /> : list.isError ? <ErrorState message={list.error.message} onRetry={() => void list.refetch()} /> : !list.data?.items.length ? (
          <EmptyState title={channel === "whatsapp" ? "No hay campañas de WhatsApp" : "No hay campañas"} {...(channel === "email" && isAdmin && !term && !status ? { body: "Crea tu primera campaña de email con “Nueva campaña”." } : {})} />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[900px]">
              <thead><tr>
                <th className="th">Campaña</th><th className="th">Creador</th><th className="th">Estado</th><th className="th">Fecha de envío</th>
                <th className="th text-right">Entregados</th><th className="th text-right">{channel === "whatsapp" ? "Leídos" : "Abiertos"}</th><th className="th text-right">Clics</th><th className="th w-24" />
              </tr></thead>
              <tbody>
                {list.data.items.map((c) => {
                  const st = c.stats ?? {};
                  const recipients = st.recipients ?? st.sent ?? 0;
                  const delivered = st.delivered ?? st.sent ?? 0;
                  const isDraft = c.status === "draft";
                  const href = isDraft && isAdmin && c.source === "tiger" ? `/marketing/campanas/${c.id}/editar` : `/marketing/campanas/${c.id}`;
                  const metric = (n: number | undefined, base: number, m: string): JSX.Element => isDraft ? <span className="text-ink-muted">—</span> : (
                    <Link to={`/marketing/campanas/${c.id}?m=${m}`} className="hover:underline"><span className="tnum text-ink">{(n ?? 0).toLocaleString("es-CO")}</span> <span className="text-xs text-ink-muted tnum">{pct(n, base)}</span></Link>
                  );
                  return (
                    <tr key={c.id} className="hover:bg-surface-hover">
                      <td className="td">
                        <Link to={href} className="flex items-center gap-3 min-w-0">
                          {c.hasHtml ? <EmailThumb src={`/api/v1/mkt/campaigns/${c.id}/preview`} /> : <div className="w-9 h-11 rounded border border-line bg-canvas grid place-items-center text-ink-muted shrink-0" title="Sin vista previa"><Mail size={15} strokeWidth={1.75} /></div>}
                          <div className="min-w-0">
                            <div className="flex items-center gap-1.5"><span className="font-medium text-ink truncate max-w-[220px]">{c.name}</span>{c.source === "clientify" && <Badge color="purple">Clientify</Badge>}</div>
                            <div className="text-xs text-ink-muted truncate max-w-[280px]">{c.subject ?? "—"}</div>
                            {c.folder && <div className="mt-0.5"><Badge>{c.folder}</Badge></div>}
                          </div>
                        </Link>
                      </td>
                                            <td className="td"><div className="max-w-[110px] truncate" title={c.creator ?? undefined}>{c.creator ?? "—"}</div></td>
                      <td className="td"><Badge color={STATUS_LABEL[c.status]?.color ?? "gray"}>{STATUS_LABEL[c.status]?.label ?? c.status}</Badge></td>
                      <td className="td whitespace-nowrap">{fmtDate(c.sentAt ?? c.scheduledAt)}</td>
                      <td className="td text-right whitespace-nowrap">{metric(delivered, recipients, "sent")}</td>
                      <td className="td text-right whitespace-nowrap">{metric(st.uniqueOpens, delivered, "opened")}</td>
                      <td className="td text-right whitespace-nowrap">{metric(st.uniqueClicks, delivered, "clicked")}</td>
                      <td className="td">
                        {isAdmin && (
                          <div className="flex justify-end gap-0.5">
                            {isDraft && c.source === "tiger" && <Link to={`/marketing/campanas/${c.id}/editar`} className="btn-ghost btn-sm" title="Editar"><Pencil size={14} /></Link>}
                            {c.channel === "email" && c.source === "tiger" && <button className="btn-ghost btn-sm" title="Duplicar" onClick={() => dup.mutate(c.id)} disabled={dup.isPending}><Copy size={14} /></button>}
                            {(isDraft || c.status === "cancelled") && <button className="btn-ghost btn-sm text-danger" title="Eliminar" onClick={() => { if (confirm(`¿Eliminar la campaña «${c.name}»?`)) del.mutate(c.id); }}><Trash2 size={14} /></button>}
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
